import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { Subscription, finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

interface TenantCallConfig {
  tenantId: string;
  enableCall: boolean;
  enableRecording: boolean;
  enableServiceCallAutoRecording: boolean;
  available: boolean;
  syncStatus: 'Disabled' | 'Provisioning' | 'Active' | 'Failed' | 'Disabling' | string;
  providerCode?: string;
  failureCode?: string;
}

@Component({
  selector: 'tenant-call-config',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzIconModule,
    NzSpinModule,
    NzSwitchModule,
    NzTagModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-call-config.component.html',
  styleUrls: ['./tenant-call-config.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantCallConfigComponent implements OnInit, OnDestroy {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly message = inject(NzMessageService);
  private enableCallSubscription: Subscription | null = null;

  @Input() tenantId = '';

  callConfig: TenantCallConfig | null = null;
  callConfigForm: FormGroup = this.fb.group({
    enableCall: [false],
    enableRecording: [false],
    enableServiceCallAutoRecording: [false]
  });
  loading = false;
  saving = false;

  ngOnInit(): void {
    this.enableCallSubscription =
      this.callConfigForm.get('enableCall')?.valueChanges.subscribe(enabled => {
        this.updateRecordingControlState(enabled === true);
      }) ?? null;
    this.loadCallConfig();
  }

  ngOnDestroy(): void {
    this.enableCallSubscription?.unsubscribe();
  }

  loadCallConfig(): void {
    if (!this.tenantId) {
      return;
    }

    this.loading = true;
    this.http
      .post(`${ConstantUtil.TENANT_CALL_CONFIG_V1_ITEM}/${this.tenantId}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success) {
            this.message.error(this.i18n.fanyi('tenant.detail.call-config.load-failed'));
            return;
          }
          this.applyConfig(res.data);
        },
        error: () => this.message.error(this.i18n.fanyi('tenant.detail.call-config.load-failed'))
      });
  }

  saveCallConfig(): void {
    if (!this.tenantId) {
      return;
    }

    const values = this.callConfigForm.getRawValue();
    const requestData = {
      tenantId: this.tenantId,
      enableCall: values.enableCall === true,
      enableRecording: values.enableRecording === true,
      enableServiceCallAutoRecording: values.enableServiceCallAutoRecording === true
    };

    this.saving = true;
    this.http
      .post(ConstantUtil.TENANT_CALL_CONFIG_V1_UPDATE, requestData)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (!res?.success) {
            this.message.error(this.i18n.fanyi('tenant.detail.call-config.save-failed'));
            return;
          }
          this.applyConfig(res.data ?? { ...this.callConfig, ...requestData });
          this.message.success(this.i18n.fanyi('tenant.detail.call-config.save-success'));
        },
        error: () => this.message.error(this.i18n.fanyi('tenant.detail.call-config.save-failed'))
      });
  }

  resetCallConfig(): void {
    if (!this.callConfig) {
      return;
    }
    this.patchForm(this.callConfig);
    this.message.info(this.i18n.fanyi('tenant.detail.call-config.reset-success'));
  }

  getSyncStatusColor(status: string | null | undefined): string {
    const colors: Record<string, string> = {
      Active: 'success',
      Provisioning: 'processing',
      Failed: 'error',
      Disabling: 'warning',
      Disabled: 'default'
    };
    return colors[status ?? ''] ?? 'default';
  }

  get syncStatusKey(): string {
    return `tenant.detail.call-config.sync-status.${String(this.callConfig?.syncStatus ?? 'unknown').toLowerCase()}`;
  }

  private applyConfig(config: TenantCallConfig): void {
    this.callConfig = config;
    this.patchForm(config);
    this.cdr.detectChanges();
  }

  private patchForm(config: Partial<TenantCallConfig>): void {
    this.callConfigForm.patchValue(
      {
        enableCall: config.enableCall === true,
        enableRecording: config.enableRecording === true,
        enableServiceCallAutoRecording: config.enableServiceCallAutoRecording === true
      },
      { emitEvent: false }
    );
    this.updateRecordingControlState(config.enableCall === true);
    this.callConfigForm.markAsPristine();
  }

  private updateRecordingControlState(enableCall: boolean): void {
    ['enableRecording', 'enableServiceCallAutoRecording'].forEach(controlName => {
      const control = this.callConfigForm.get(controlName);
      if (enableCall) {
        control?.enable({ emitEvent: false });
      } else {
        control?.disable({ emitEvent: false });
      }
    });
  }
}
